import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileRecordList, type MobileRecordCard } from '../components/mobile/MobileRecordList';

// ── Types ─────────────────────────────────────────────────────

interface Registration {
  id: string;
  userId: string;
  eventId: string;
  role: string;
  status: string;
  createdAt: string;
  user: { fullName: string; email: string };
  event: { name: string };
  ticket?: {
    tierName: string;
    status: string;
    ticketNumber?: number;
    /** Pre-formatted ticket label (e.g. "TKT-042") for guest-record rows. */
    ticketLabel?: string;
    mealOption?: { name: string } | null;
    order?: {
      id: string;
      referenceId: string;
      purchaserName: string;
      purchaserEmail: string;
      purchaserPhone: string;
      ticketCount: number;
      status: string;
      createdAt: string;
    } | null;
  } | null;
  mealSelection?: { mealOption: { name: string } } | null;
  seatAssignment?: { tableNumber: number; seatNumber: number } | null;
}

interface OrderDetail {
  id: string;
  referenceId: string;
  purchaserName: string;
  purchaserEmail: string;
  purchaserPhone: string;
  ticketCount: number;
  status: string;
  createdAt: string;
  tier: { name: string; price: number };
  tickets: {
    id: string;
    ticketNumber: number;
    guestName: string;
    tierName: string;
    priceAtPurchase: number;
    status: string;
    mealOption: { name: string } | null;
  }[];
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  pageMobile: {
    padding: '1rem',
    paddingBottom: 'calc(72px + env(safe-area-inset-bottom))',
    maxWidth: '100%',
  },
  sectionMobile: {
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    overflowX: 'visible' as const,
  },
  titleMobile: { fontSize: '1.25rem', margin: '0 0 1rem 0' },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: '0 0 1.5rem 0',
    fontWeight: 700,
  },
  filterContainer: {
    marginBottom: '0.75rem',
  },
  filterInput: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.9rem',
    border: '1px solid #d0e8d6',
    borderRadius: '6px',
    width: '100%',
    maxWidth: '360px',
    outline: 'none',
  },
  filterPills: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
  },
  pill: {
    padding: '0.35rem 0.75rem',
    border: '1px solid #d0d0d0',
    borderRadius: '16px',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.15s',
  },
  pillActive: {
    padding: '0.35rem 0.75rem',
    border: '1px solid #1a6b3c',
    borderRadius: '16px',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.15s',
  },
  pillCount: {
    fontSize: '0.7rem',
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: '8px',
    padding: '1px 5px',
    minWidth: '18px',
    textAlign: 'center' as const,
  },
  section: {
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
    lineHeight: 1.4,
  },
  th: {
    padding: '0.6rem 0.75rem',
    textAlign: 'left' as const,
    backgroundColor: '#f0f7f2',
    borderBottom: '2px solid #1a6b3c',
    fontWeight: 600,
    color: '#0f2d1f',
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #e8e8e8',
    color: '#333',
    verticalAlign: 'top' as const,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.95rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#888',
    fontSize: '0.9rem',
    fontStyle: 'italic' as const,
  },
  error: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#c0392b',
    fontSize: '0.9rem',
  },
  orderLink: {
    color: '#1a6b3c',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 500,
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modalBox: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto' as const,
    position: 'relative' as const,
  },
  modalCloseBtn: {
    position: 'absolute' as const,
    top: '0.75rem',
    right: '1rem',
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666',
    lineHeight: 1,
    padding: '4px 8px',
    borderRadius: '4px',
  },
  modalTitle: {
    color: '#1a6b3c',
    fontSize: '1.25rem',
    fontWeight: 700,
    margin: '0 0 1.25rem 0',
  },
  modalSectionTitle: {
    color: '#1a6b3c',
    fontSize: '1rem',
    fontWeight: 600,
    margin: '1.25rem 0 0.75rem 0',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem 1.5rem',
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  infoLabel: {
    color: '#666',
    fontSize: '0.8rem',
    marginBottom: '2px',
  },
  infoValue: {
    color: '#333',
    fontWeight: 500,
  },
};

// ── Component ─────────────────────────────────────────────────

export function RegistrationsPage() {
  const { canEdit } = usePageAuthorization('registrations');
  const isMobile = useIsMobile();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'no-meal' | 'patron' | 'general' | 'no-seat'>('all');

  // Order detail modal state
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchRegistrations() {
      setLoading(true);
      setError(null);

      try {
        const data = await apiClient<Registration[]>('/registrations');
        if (!cancelled) {
          // Sort by registered date, newest first
          const sorted = data.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setRegistrations(sorted);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load registrations';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchRegistrations();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch order details when an order is selected
  useEffect(() => {
    if (!selectedOrderId) {
      setOrderDetail(null);
      setOrderError(null);
      return;
    }

    let cancelled = false;

    async function fetchOrderDetail() {
      setOrderLoading(true);
      setOrderError(null);

      try {
        const data = await apiClient<OrderDetail>(`/legacy-ball/orders/${selectedOrderId}`);
        if (!cancelled) {
          setOrderDetail(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load order details';
          setOrderError(message);
        }
      } finally {
        if (!cancelled) {
          setOrderLoading(false);
        }
      }
    }

    fetchOrderDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedOrderId]);

  const filtered = registrations.filter((reg) => {
    // Text search
    if (filter) {
      const term = filter.toLowerCase();
      const matchesSearch = reg.user.fullName.toLowerCase().includes(term) ||
        reg.event.name.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    // Category filter
    switch (activeFilter) {
      case 'no-meal':
        return !reg.mealSelection && !reg.ticket?.mealOption;
      case 'patron':
        return reg.ticket?.tierName?.toLowerCase().includes('patron');
      case 'general':
        return reg.ticket?.tierName?.toLowerCase().includes('general') || !reg.ticket?.tierName;
      case 'no-seat':
        return !reg.seatAssignment;
      default:
        return true;
    }
  });

  const hasMeal = (reg: Registration) => !!(reg.mealSelection || reg.ticket?.mealOption);

  function handleOrderClick(orderId: string) {
    setSelectedOrderId(orderId);
  }

  function handleCloseModal() {
    setSelectedOrderId(null);
  }

  function renderContent() {
    if (loading) {
      return <div style={styles.loading}>Loading registrations...</div>;
    }

    if (error) {
      return <div style={styles.error}>{error}</div>;
    }

    if (filtered.length === 0) {
      return <div style={styles.emptyState}>No registrations found</div>;
    }

    // Mobile: fold the 9-column table into tap-through cards. Tapping a card
    // opens the order modal (same action as the desktop Order # link).
    if (isMobile) {
      const cards: MobileRecordCard[] = filtered.map((reg) => {
        const ticketNo = reg.ticket?.ticketLabel
          ? reg.ticket.ticketLabel
          : reg.ticket?.ticketNumber
          ? `TKT-${String(reg.ticket.ticketNumber).padStart(3, '0')}`
          : null;
        const tier = reg.ticket?.tierName || null;
        const seat = reg.seatAssignment
          ? `Table ${reg.seatAssignment.tableNumber}, Seat ${reg.seatAssignment.seatNumber}`
          : 'Unassigned';
        const meal = reg.mealSelection?.mealOption?.name || reg.ticket?.mealOption?.name || null;
        const orderRef = reg.ticket?.order?.referenceId;

        const line1 = [ticketNo, tier, seat].filter(Boolean).join(' · ');
        const line2Parts = [
          orderRef ? `Order ${orderRef}` : null,
          meal || 'No meal',
          `reg. ${new Date(reg.createdAt).toLocaleDateString()}`,
        ].filter(Boolean) as string[];

        return {
          id: reg.id,
          title: reg.user.fullName,
          status: { label: reg.status, tone: 'neutral' },
          lines: [line1, line2Parts.join(' · ')].filter(Boolean),
        };
      });

      // Map card id (registration id) -> order id so a tap opens the modal.
      const orderIdByReg = new Map(
        filtered.map((reg) => [reg.id, reg.ticket?.order?.id ?? null]),
      );

      return (
        <MobileRecordList
          records={cards}
          onSelect={(regId) => {
            const orderId = orderIdByReg.get(regId);
            if (orderId) handleOrderClick(orderId);
          }}
          emptyText="No registrations found"
        />
      );
    }

    return (
      <table style={styles.table} className="reports-table">
        <thead>
          <tr>
            <th style={styles.th}>User Name</th>
            <th style={styles.th}>Order #</th>
            <th style={styles.th}>Event</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Ticket #</th>
            <th style={styles.th}>Ticket Tier</th>
            <th style={styles.th}>Meal Selection</th>
            <th style={styles.th}>Seat Assignment</th>
            <th style={styles.th}>Registered Date</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((reg, idx) => (
            <tr key={reg.id} style={{ backgroundColor: !hasMeal(reg) ? '#fff3e0' : idx % 2 === 0 ? '#ffffff' : '#f7faf8' }}>
              <td style={styles.td}>{reg.user.fullName}</td>
              <td style={styles.td}>
                {reg.ticket?.order ? (
                  <span
                    style={styles.orderLink}
                    onClick={() => handleOrderClick(reg.ticket!.order!.id)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.textDecoration = 'none';
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOrderClick(reg.ticket!.order!.id);
                      }
                    }}
                    aria-label={`View order ${reg.ticket.order.referenceId}`}
                  >
                    {reg.ticket.order.referenceId}
                  </span>
                ) : (
                  '—'
                )}
              </td>
              <td style={styles.td}>{reg.event.name}</td>
              <td style={styles.td}>{reg.status}</td>
              <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {reg.ticket?.ticketLabel ? reg.ticket.ticketLabel : reg.ticket?.ticketNumber ? `TKT-${String(reg.ticket.ticketNumber).padStart(3, '0')}` : '—'}
              </td>
              <td style={styles.td}>{reg.ticket?.tierName || '—'}</td>
              <td style={styles.td}>{reg.mealSelection?.mealOption?.name || reg.ticket?.mealOption?.name || '—'}</td>
              <td style={styles.td}>
                {reg.seatAssignment
                  ? `Table ${reg.seatAssignment.tableNumber}, Seat ${reg.seatAssignment.seatNumber}`
                  : '—'}
              </td>
              <td style={styles.td}>
                {new Date(reg.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderOrderModal() {
    if (!selectedOrderId) return null;

    return (
      <div style={styles.modalOverlay} onClick={handleCloseModal}>
        <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
          <button
            style={styles.modalCloseBtn}
            onClick={handleCloseModal}
            aria-label="Close order details"
          >
            ✕
          </button>

          {orderLoading && (
            <div style={styles.loading}>Loading order details...</div>
          )}

          {orderError && (
            <div style={styles.error}>{orderError}</div>
          )}

          {orderDetail && !orderLoading && (
            <>
              <h2 style={styles.modalTitle}>Order {orderDetail.referenceId}</h2>

              <div style={styles.infoGrid}>
                <div>
                  <div style={styles.infoLabel}>Purchaser Name</div>
                  <div style={styles.infoValue}>{orderDetail.purchaserName}</div>
                </div>
                <div>
                  <div style={styles.infoLabel}>Email</div>
                  <div style={styles.infoValue}>{orderDetail.purchaserEmail}</div>
                </div>
                <div>
                  <div style={styles.infoLabel}>Phone</div>
                  <div style={styles.infoValue}>{orderDetail.purchaserPhone || '—'}</div>
                </div>
                <div>
                  <div style={styles.infoLabel}>Ticket Count</div>
                  <div style={styles.infoValue}>{orderDetail.ticketCount}</div>
                </div>
                <div>
                  <div style={styles.infoLabel}>Status</div>
                  <div style={styles.infoValue}>{orderDetail.status}</div>
                </div>
                <div>
                  <div style={styles.infoLabel}>Created</div>
                  <div style={styles.infoValue}>
                    {new Date(orderDetail.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <h3 style={styles.modalSectionTitle}>Tickets</h3>

              {orderDetail.tickets.length === 0 ? (
                <div style={styles.emptyState}>No tickets found for this order</div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Ticket #</th>
                      <th style={styles.th}>Guest Name</th>
                      <th style={styles.th}>Tier</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Meal Option</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetail.tickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          TKT-{String(ticket.ticketNumber).padStart(3, '0')}
                        </td>
                        <td style={styles.td}>{ticket.guestName || '—'}</td>
                        <td style={styles.td}>{ticket.tierName || '—'}</td>
                        <td style={styles.td}>{ticket.status}</td>
                        <td style={styles.td}>{ticket.mealOption?.name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.page, ...(isMobile ? styles.pageMobile : null) }}>
      <h1 style={{ ...styles.title, ...(isMobile ? styles.titleMobile : null) }}>Registrations</h1>

      <div style={styles.filterContainer}>
        <input
          type="text"
          placeholder="Filter by user name or event name..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={styles.filterInput}
        />
      </div>

      <div style={styles.filterPills}>
        {([
          { key: 'all', label: 'All' },
          { key: 'no-meal', label: 'No Meal' },
          { key: 'patron', label: 'Patron' },
          { key: 'general', label: 'General' },
          { key: 'no-seat', label: 'No Seat' },
        ] as const).map((f) => (
          <button
            key={f.key}
            style={activeFilter === f.key ? styles.pillActive : styles.pill}
            onClick={() => setActiveFilter(f.key)}
          >
            {f.label}
            {f.key !== 'all' && (
              <span style={styles.pillCount}>
                {f.key === 'no-meal' ? registrations.filter(r => !r.mealSelection && !r.ticket?.mealOption).length
                  : f.key === 'patron' ? registrations.filter(r => r.ticket?.tierName?.toLowerCase().includes('patron')).length
                  : f.key === 'general' ? registrations.filter(r => r.ticket?.tierName?.toLowerCase().includes('general') || !r.ticket?.tierName).length
                  : registrations.filter(r => !r.seatAssignment).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ ...styles.section, ...(isMobile ? styles.sectionMobile : null) }}>
        {renderContent()}
      </div>

      {renderOrderModal()}
    </div>
  );
}
